import * as React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  Container,
  createStyles,
  makeStyles,
  Typography,
  Theme,
  Tooltip,
  Button,
} from "@material-ui/core";
import Loader from "react-loader-spinner";
import SearchIcon from "@material-ui/icons/Search";
import SimpleTable from "../SimpleTable";
import { SelectOptions } from "../../core/models/SelectOptions";
import { searchPageType } from "../../core/constants/searchPagesOptions";
import { Skeleton } from "@material-ui/lab";
import DeleteIcon from "@material-ui/icons/Delete";
import SmartSelect from "../../features/SmartFilters/SmartSelect";
import { IEnumerable } from "../../core/models/ModelHelpers";
import InstallationGroup from "../../core/models/InstallationGroup";
import User from "../../core/models/User";
import InstallationModel from "../../core/models/Installation";
import { isArrayValid } from "../../helpers/utility";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      "display": "flex",
      "margin": theme.spacing(2, 1),
      "& *": {
        color: theme.palette.primary.main,
      },
    },

    searchWrapper: {
      padding: theme.spacing(2),
    },

    accordion: {
      "background": theme.customPaper.background.cardBackgroundLight,

      "& h3": {
        color: theme.customPalette.text.primary,
        fontWeight: "bold",
      },
    },

    spinner: {
      padding: "0px 0px 0px 20px",
    },

    tableWrapper: {
      "flex": 1,
      "display": "flex",
      "flexDirection": "column",
      "maxHeight": "25vh",
      "overflowY": "auto",
      "rowGap": theme.spacing(1),

      // first child
      "&:nth-child(1)": {
        maxHeight: "55vh",
      },
    },
  })
);

type SearchPageMainComponentProps = {
  searchTitle: string;
  searchLabel: string;
  primaryLabel: string;
  primarySearchHeaders: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  primaryResult: any[][];
  secondaryLabel: string;
  secondarySearchHeaders: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  secondaryResult: any[][];
  options: SelectOptions[];
  loading?: boolean;
  selectCustomRenderLabel?: searchPageType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedOption: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setSelectedOption: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleOpenDeleteModal?: (selectedRecords: any[]) => void;
  hasId?: boolean;
};

type EditInstallationGroup = Omit<Omit<InstallationGroup, "id">, "createUser"> & {
  id?: IEnumerable;
  createUser?: User;
  installations?: InstallationModel[];
};

function SearchPageMainComponent(props: SearchPageMainComponentProps) {
  const {
    searchTitle,
    searchLabel,
    loading,
    primaryLabel,
    primaryResult,
    primarySearchHeaders,
    secondaryLabel,
    secondaryResult,
    secondarySearchHeaders,
    options,
    selectCustomRenderLabel,
    selectedOption,
    setSelectedOption,
    handleOpenDeleteModal,
    hasId,
  } = props;

  const classes = useStyles();

  const [selectedPrimaryResults, setSelectedPrimaryResults] = React.useState<any[]>([]);
  const selectedPrimaryResultsIsValid = React.useMemo(() => isArrayValid(selectedPrimaryResults), [
    selectedPrimaryResults,
  ]);

  const areAllPrimaryResultsSelected = React.useMemo(() => {
    if (!selectedPrimaryResultsIsValid || !isArrayValid(primaryResult)) {
      return false;
    }

    return primaryResult?.every((x) => selectedPrimaryResults.includes(x));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryResult, selectedPrimaryResults]);

  const handleClickSelectAllPrimaryResults = () => {
    // Empty selection → select all
    if (!selectedPrimaryResultsIsValid) {
      return setSelectedPrimaryResults(primaryResult);
    }

    // all users selected → deselect all
    if (areAllPrimaryResultsSelected) {
      return setSelectedPrimaryResults([]);
    }

    // some are selected → select all
    return setSelectedPrimaryResults(primaryResult);
  };

  const handleSelectPrimaryResult = (primaryResultId: IEnumerable | undefined) => {
    if (selectedPrimaryResults.includes(primaryResultId)) {
      return setSelectedPrimaryResults(selectedPrimaryResults.filter((x) => x !== primaryResultId));
    }

    return setSelectedPrimaryResults([...selectedPrimaryResults, primaryResultId]);
  };

  const handleClickDeleteBtn = () => {
    if (handleOpenDeleteModal) handleOpenDeleteModal(selectedPrimaryResults);
  };

  function customSelectLabelRender(option: SelectOptions) {
    if (option.name) {
      return `${option.name} (${
        selectCustomRenderLabel
          ? selectCustomRenderLabel === "installation"
            ? "#Nº White: "
            : selectCustomRenderLabel === "user"
            ? "E-mail: "
            : ""
          : ""
      }${option.label})`;
    }

    return option.label;
  }

  return (
    <Container maxWidth="xl">
      {/* Header */}
      <div className={classes.title}>
        <SearchIcon color="primary" />
        <Typography>{searchTitle}</Typography>
      </div>
      <Card elevation={1}>
        {/* Search Field */}
        <div className={classes.searchWrapper}>
          {searchLabel}
          {loading ? (
            <Skeleton height={40} />
          ) : (
            <SmartSelect
              options={options}
              getOptionLabel={customSelectLabelRender}
              value={selectedOption}
              onChange={setSelectedOption}
            />
          )}
        </div>

        {/* Primary Search Results */}
        <Accordion defaultExpanded>
          <AccordionSummary className={classes.accordion}>
            <Typography component="h3">{primaryLabel}</Typography>
            {loading && (
              <div className={classes.spinner}>
                <Loader type="TailSpin" color="#00AB66" height={15} width={15} />
              </div>
            )}
          </AccordionSummary>
          <AccordionDetails>
            <div className={classes.tableWrapper}>
              <div>
                <Tooltip
                  title={
                    selectedPrimaryResultsIsValid
                      ? "Excluir os usuários do grupo das instalações selecionadas"
                      : "Nenhum grupo selecionado"
                  }
                >
                  <span>
                    <Button
                      onClick={() => handleClickDeleteBtn()}
                      disabled={!selectedPrimaryResultsIsValid}
                      variant="outlined"
                    >
                      <DeleteIcon />
                      Excluir Selecionados
                    </Button>
                  </span>
                </Tooltip>
              </div>

              <SimpleTable
                removeCheckBox={true}
                onClickRemoveAll={() => handleClickSelectAllPrimaryResults()}
                areAllSelected={areAllPrimaryResultsSelected}
                selectedRows={selectedPrimaryResults}
                handleSelect={handleSelectPrimaryResult}
                header={primarySearchHeaders}
                rows={primaryResult}
                hasId={hasId}
              />
            </div>
          </AccordionDetails>
        </Accordion>

        {/* Secondary Search Results */}
        <Accordion defaultExpanded>
          <AccordionSummary className={classes.accordion}>
            <Typography component="h3">{secondaryLabel}</Typography>
            {loading && (
              <div className={classes.spinner}>
                <Loader type="TailSpin" color="#00AB66" height={15} width={15} />
              </div>
            )}
          </AccordionSummary>
          <AccordionDetails>
            <div className={classes.tableWrapper}>
              <SimpleTable header={secondarySearchHeaders} rows={secondaryResult} hasId={hasId} />
            </div>
          </AccordionDetails>
        </Accordion>
      </Card>
    </Container>
  );
}

export default SearchPageMainComponent;
